"use client";

import { useContext, type ReactElement, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatStake, formatUsd } from "@/components/odds-screen/live/labels";
import { formatOdds } from "@/components/fantasy-props/model";
import type { Stake, StakeQuote } from "@/lib/book-stake";
import type { OddsFormat } from "@/utils/calculators/odds-utils";
import { WhalesColumnShown } from "@/hooks/use-whales-gate";

export function stakeLines(quote: StakeQuote, format: OddsFormat, whales: boolean): readonly string[] {
  const { stake, odds, side, other } = quote;
  const usd = formatUsd(stake.usd);
  const price = formatOdds(odds, format);
  if (stake.kind === "limit") return [`Max bet ${usd} at ${price}.`];
  const mine = side === null ? "this side" : `the ${side}`;
  const their = other === null ? "the other side" : `the ${other}`;
  const source = `That money comes from sharps who want ${their}.`;
  return [`You can bet up to ${usd} on ${mine} at ${price}.`, whales ? `${source} The Whales price on ${their} shows what they want.` : source];
}

export function stakeSpoken(stake: Stake | null): string {
  return stake === null ? "" : `, ${formatStake(stake.usd, stake.kind).spoken}`;
}

export function StakeLabel({ stake, className }: { stake: Stake; className?: string }) {
  return (
    <span className={cn("data-num whitespace-nowrap text-[10px] font-medium leading-3 tabular-nums text-zinc-500", className)} aria-hidden="true">
      {formatStake(stake.usd, stake.kind).text}
    </span>
  );
}

function StakeText({ quote, format }: { quote: StakeQuote; format: OddsFormat }) {
  const whales = useContext(WhalesColumnShown);
  return stakeLines(quote, format, whales).map((line, i) => <p key={i}>{line}</p>);
}

export function HoverTip({ content, className, children }: { content: ReactNode; className: string; children: ReactElement }) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip disableHoverableContent>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        {content !== null && (
          <TooltipContent side="top" className={className}>
            {content}
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

export function TapTip({ label, content, className, contentClassName, children }: { label: string; content: ReactNode; className: string; contentClassName: string; children: ReactNode }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          aria-label={label}
          className={className}
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="end" className={contentClassName} onClick={(event) => event.stopPropagation()}>
        {content}
      </PopoverContent>
    </Popover>
  );
}

export function StakeTip({ quote, format, children }: { quote: StakeQuote | null; format: OddsFormat; children: ReactElement }) {
  return (
    <HoverTip content={quote === null ? null : <StakeText quote={quote} format={format} />} className="max-w-64 space-y-1 text-xs leading-snug">
      {children}
    </HoverTip>
  );
}

export function StakeButton({ quote, format }: { quote: StakeQuote; format: OddsFormat }) {
  const { text, spoken } = formatStake(quote.stake.usd, quote.stake.kind);
  return (
    <TapTip
      label={`${spoken}, what this means`}
      content={<StakeText quote={quote} format={format} />}
      className="data-num inline-flex min-h-6 items-center rounded px-1 text-[10px] font-medium leading-3 tabular-nums text-zinc-500 underline decoration-dotted underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      contentClassName="w-64 space-y-1 p-3 text-xs leading-snug"
    >
      {text}
    </TapTip>
  );
}
